import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  InputLabel,
  Radio,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { getAllInstitutions } from "../../../../server/server";
import { useUser } from "../../../../auth/UseUser";
import { FaFileWord } from "react-icons/fa";

type FilterFormProps = {
  term: string;
  setTerm: (t: string) => void;
  defaultInstitution?: any | null;
  onInstitutionChange: (event: any, newValue: any) => void;
  totalPlannedCount: number;
  unrealisedCount: number;
  status: "planned" | "unrealised" | "";
  setStatus: React.Dispatch<
    React.SetStateAction<"planned" | "unrealised" | "">
  >;
  measure?: boolean;
  fileLoading?: boolean;
  downloadWord?: () => void;
  reportType?: string;
  setReportType?: React.Dispatch<React.SetStateAction<string>>;
};

const FilterForm: React.FC<FilterFormProps> = ({
  term,
  setTerm,
  defaultInstitution,
  onInstitutionChange,
  totalPlannedCount,
  unrealisedCount,
  status,
  setStatus,
  measure = false,
  fileLoading = false,
  downloadWord = () => {},
  reportType = "",
  setReportType = () => {},
}) => {
  const [institutions, setInstitutions] = useState<any[]>([]);
  const { user } = useUser();

  useEffect(() => {
    const loadInstitutions = async () => {
      const resp = await getAllInstitutions(1);
      if (resp?.status) {
        setInstitutions(resp.data ?? []);
      } else {
        setInstitutions([]);
      }
    };
    loadInstitutions();
  }, []);

  return (
    <div className="rounded-lg border border-[#81848f3d] p-8 pb-4 mt-8 flex-grow">
      <div className="flex justify-between h-full">
        <div className="w-full">
          <h1 className="text-[#04438bae] text-3xl font-bold">Претрага</h1>
          <div className="flex flex-row justify-between items-center w-full">
            <div className="w-full mt-2 flex flex-wrap items-start gap-6">
              <div className="flex flex-col items-start gap-3">
                <p className="w-[180px] text-sm text-[#404040]">
                  По називу/идентификацији
                </p>
                <TextField
                  label={measure ? "Назив мере" : "Назив прописа"}
                  id="outlined-size-small"
                  size="small"
                  autoComplete="off"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                />
              </div>
              <div className="flex flex-col items-start gap-3">
                <p className="w-[160px] text-sm text-[#404040]">
                  По институцији
                </p>
                <Autocomplete
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    );
                  }}
                  value={defaultInstitution ?? null}
                  size="small"
                  onChange={onInstitutionChange}
                  id="controllable-states-demo"
                  options={institutions}
                  getOptionLabel={(option) => option?.name ?? ""}
                  sx={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Институција" />
                  )}
                  noOptionsText={"Нема резултата"}
                  disabled={
                    !!defaultInstitution &&
                    user &&
                    user.roleView?.name !== "Администратор"
                  }
                />
              </div>
              <div className="flex flex-col items-start">
                <p className="w-[160px] text-sm text-[#404040]">
                  {measure ? "По статусу мере" : "По статусу прописа"}
                </p>
                <div className="flex flex-col">
                  {/* PLANNED CHECKBOX */}
                  <div className="flex items-center">
                    <Checkbox
                      checked={status === "planned" || status === ""}
                      onChange={() => {
                        if (status === "planned") return;
                        if (status === "") setStatus("unrealised");
                        else if (status === "unrealised") setStatus("");
                        else setStatus("planned");
                      }}
                    />
                    <p className="inline-block ml-2">
                      Планирано за текући период ({totalPlannedCount})
                    </p>
                  </div>

                  {/* UNREALISED CHECKBOX */}
                  <div className="flex items-center">
                    <Checkbox
                      checked={status === "unrealised" || status === ""}
                      onChange={() => {
                        if (status === "unrealised") return;
                        if (status === "") setStatus("planned");
                        else if (status === "planned") setStatus("");
                        else setStatus("unrealised");
                      }}
                    />
                    <p className="inline-block ml-2">
                      Нереализовано од почетка примене НПАА ({unrealisedCount})
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-l border-[#81848f3d] pl-4  flex flex-col">
          <h1 className="text-[#04438bae] text-3xl font-bold">Извоз</h1>
          <p className="w-[160px] text-sm text-[#404040] invisible">
            По институцији
          </p>
          {!measure && (
            <div className="flex flex-row items-center gap-8 w-full min-w-[250px]">
              <div className="flex flex-col">
                <div className="flex items-center">
                  <Radio
                    checked={reportType === "cluster"}
                    onChange={() => {
                      setReportType("cluster");
                    }}
                  />
                  <p className="inline-block ml-2">По кластерима</p>
                </div>

                <div className="flex items-center">
                  <Radio
                    checked={reportType === "institution"}
                    onChange={() => {
                      setReportType("institution");
                    }}
                  />
                  <p className="inline-block ml-2">По институцијама</p>
                </div>
              </div>

              <div className="flex justify-center items-center w-8 h-8">
                {fileLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <FaFileWord
                    className="text-[#043e81ae] w-8 h-8 cursor-pointer hover:text-[#043e81]"
                    onClick={downloadWord}
                  />
                )}
              </div>
            </div>
          )}
          {measure && (
            <div className="flex flex-row items-center gap-8 w-full min-w-[250px]">
              <div className="flex flex-col">
                <div className="flex items-center">
                  <Radio
                    checked={reportType === "cluster"}
                    onChange={() => setReportType("cluster")}
                  />
                  <p className="inline-block ml-2">По кластерима</p>
                </div>

                {/* <div className="flex items-center">
                <Radio
                  checked={reportType === "institution"}
                  onChange={() => setReportType("institution")}
                />
                <p className="inline-block ml-2">По институцијама</p>
              </div> */}

                <div className="flex items-center">
                  <Radio
                    checked={reportType === "clusterAndInstitution"}
                    onChange={() => setReportType("clusterAndInstitution")}
                  />
                  <p className="inline-block ml-2">По институцијама</p>
                </div>
              </div>

              <div className="flex justify-center items-center w-8 h-8">
                {fileLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <FaFileWord
                    className="text-[#043e81ae] w-8 h-8 cursor-pointer hover:text-[#043e81]"
                    onClick={downloadWord}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterForm;
